import type {
  Order,
  OrderLineItem,
  OrderWithDetails,
  ShipmentWithScans,
} from "@estelle/domain";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink, PackageSearch, Truck } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { MouseEvent, PointerEvent } from "react";

import { useDataSource } from "@/adapters/data-source";
import { can, usePermissions } from "@/auth/permissions";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { safeWebHref } from "@/lib/safe-href";
import { cn } from "@/lib/utils";
import {
  ExactTime,
  HealthLabel,
  StageBadge,
} from "@/modules/orders/screens/TrackingParts";
import { scanWords } from "@/modules/orders/tracking";

/**
 * An order's items and tracking, without opening the order (#489).
 *
 * A small icon beside the order number. Hovering it with a mouse opens the
 * popover after a short pause; a tap, a click or Enter/Space opens it and
 * keeps it open until it is dismissed. Escape closes it and puts focus back on
 * the icon.
 *
 * Nothing is fetched for the list. The popover's body mounts only while the
 * popover is open, and it reads the same `orders.get` the order drawer does,
 * for that one order — under the same query key, so opening the order right
 * after is served from what the popover already loaded.
 *
 * Where it opens depends on what opened it (#517). From a mouse it opens to the
 * right of the icon: below, it covered the icons of the rows underneath, so
 * moving straight down from one icon to the next never reached the next. From a
 * finger or a key it has to open above or below, and takes the side with more
 * room — see `Side` for why that is chosen here and not left to flip.
 */

/** How long the pointer rests on the icon before a hover opens the popover. */
const HOVER_OPEN_MS = 250;
/** Long enough to cross the gap between the icon and the popover. */
const HOVER_CLOSE_MS = 150;

/** How the popover was opened, which decides how it may close. */
type Opened = "hover" | "pinned";
type Input = "mouse" | "touch" | "keyboard";

/**
 * Which side of the icon the popover opens on, fixed when it opens.
 *
 * Not left to Radix's flip. The popover's body is a skeleton for the first
 * moments and its real height arrives later, and the content's own
 * `max-height` is the room Radix last measured below it: once the data lands,
 * flip sees a popover that already "fits" and stays where it was, squeezed to
 * a sliver at the foot of the window (#517 saw about 165px for an order with
 * six shipments). Choosing the roomier side up front makes that clamp
 * harmless: it can only ever squeeze content that has nowhere better to go.
 */
type Side = "right" | "top" | "bottom";

export function OrderQuickView({
  order,
  className,
}: {
  order: Pick<Order, "id" | "orderNumber">;
  className?: string;
}) {
  const permissions = usePermissions();

  // Hiding the icon is a courtesy. `GET /api/orders/:id` checks the same
  // permission itself, so a hidden icon is not what keeps the data back.
  if (!can(permissions, "orders.detail.view")) return null;

  return <QuickViewPopover order={order} className={className} />;
}

function QuickViewPopover({
  order,
  className,
}: {
  order: Pick<Order, "id" | "orderNumber">;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  /**
   * Modal only for a touch tap. A tap outside a modal popover just dismisses
   * it; without that, the same tap lands on the row underneath and opens the
   * order. A mouse and a keyboard keep the lighter, non-modal popover.
   */
  const [modal, setModal] = useState(false);

  const [side, setSide] = useState<Side>("bottom");

  const opened = useRef<Opened>("pinned");
  const input = useRef<Input>("keyboard");
  const icon = useRef<HTMLButtonElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  /**
   * The side for a popover about to open from `kind`: right of the icon for a
   * mouse, where it covers nothing a pointer needs next; for a finger or a key,
   * whichever of above and below has more room.
   */
  function sideFor(kind: Input): Side {
    if (kind === "mouse") return "right";
    const rect = icon.current?.getBoundingClientRect();
    if (!rect) return "bottom";
    return window.innerHeight - rect.bottom >= rect.top ? "bottom" : "top";
  }

  function cancelTimer() {
    clearTimeout(timer.current);
    timer.current = undefined;
  }

  function later(action: () => void, ms: number) {
    cancelTimer();
    timer.current = setTimeout(action, ms);
  }

  useEffect(
    () => () => {
      clearTimeout(timer.current);
    },
    [],
  );

  function onPointerEnter(event: PointerEvent) {
    if (event.pointerType !== "mouse" || open) return;
    later(() => {
      opened.current = "hover";
      setModal(false);
      setSide(sideFor("mouse"));
      setOpen(true);
    }, HOVER_OPEN_MS);
  }

  function onPointerLeave(event: PointerEvent) {
    if (event.pointerType !== "mouse") return;
    cancelTimer();
    if (open && opened.current === "hover") {
      later(() => {
        setOpen(false);
      }, HOVER_CLOSE_MS);
    }
  }

  function onClick(event: MouseEvent) {
    // Radix would toggle on its own. State is managed here instead, so that a
    // click on a popover the hover opened pins it rather than closing it.
    event.preventDefault();
    // The row is clickable too: this control must not open the order.
    event.stopPropagation();
    cancelTimer();

    if (open && opened.current === "hover") {
      opened.current = "pinned";
      return;
    }
    if (open) {
      setOpen(false);
      return;
    }
    opened.current = "pinned";
    setModal(input.current === "touch");
    setSide(sideFor(input.current));
    setOpen(true);
  }

  return (
    <Popover
      open={open}
      modal={modal}
      onOpenChange={(next) => {
        // Opening is decided above; Radix is only heard on the way out —
        // Escape, a click outside, focus moving away.
        if (!next) {
          cancelTimer();
          setOpen(false);
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          ref={icon}
          type="button"
          variant="ghost"
          size="icon-xs"
          aria-label={`Items and tracking for ${order.orderNumber}`}
          // 24px is a mouse's target. Where a finger is the input it is 36px,
          // the size the phone's cards already use (#517) — the negative
          // margin the callers give it keeps the extra off the row's height.
          className={cn(
            "text-muted-foreground shrink-0 pointer-coarse:size-9",
            className,
          )}
          onPointerDown={(event) => {
            input.current = event.pointerType === "mouse" ? "mouse" : "touch";
          }}
          onKeyDown={() => {
            input.current = "keyboard";
          }}
          onPointerEnter={onPointerEnter}
          onPointerLeave={onPointerLeave}
          onClick={onClick}
        >
          <PackageSearch className="size-3.5" aria-hidden />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        side={side}
        align="start"
        collisionPadding={16}
        aria-label={`Items and tracking for ${order.orderNumber}`}
        className={cn(
          "w-80 max-w-[calc(100vw-2rem)] overflow-y-auto p-0",
          // Beside the icon the window itself is the limit, and Radix slides
          // the popover up to fit it. Above or below, the room on that side is.
          side === "right"
            ? "max-h-[calc(100dvh-2rem)]"
            : "max-h-(--radix-popover-content-available-height)",
        )}
        // A hover must not take focus from wherever someone is typing, and
        // must not hand it back to the icon when it goes away again.
        onOpenAutoFocus={(event) => {
          if (opened.current === "hover") event.preventDefault();
        }}
        onCloseAutoFocus={(event) => {
          if (opened.current === "hover") event.preventDefault();
        }}
        onPointerEnter={(event) => {
          if (event.pointerType === "mouse") cancelTimer();
        }}
        onPointerLeave={(event) => {
          if (event.pointerType === "mouse" && opened.current === "hover") {
            later(() => {
              setOpen(false);
            }, HOVER_CLOSE_MS);
          }
        }}
        // The popover is rendered in a portal, but React still bubbles its
        // events to the row it was opened from. Without this, a click inside
        // it — on a tracking link, or on blank space — opens the order.
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <QuickViewBody order={order} />
      </PopoverContent>
    </Popover>
  );
}

/* ------------------------------------------------------------------ body */

function QuickViewBody({
  order,
}: {
  order: Pick<Order, "id" | "orderNumber">;
}) {
  const data = useDataSource();

  // The order drawer's own query key and read: one payload, one cache entry.
  const query = useQuery({
    queryKey: ["orders", "detail", order.id],
    queryFn: () => data.orders.get(order.id),
  });

  return (
    <div className="px-4 py-3" aria-live="polite" aria-busy={query.isPending}>
      <p className="text-sm font-medium">{order.orderNumber}</p>

      {query.isPending ? (
        <Pending
          // Paused is what a query does when there is no network: it is not
          // fetching, and will not until there is.
          offline={query.fetchStatus === "paused"}
          onRetry={() => {
            void query.refetch();
          }}
        />
      ) : query.isError ? (
        <div className="mt-2 space-y-2">
          <p className="text-destructive text-sm">Could not load this order.</p>
          <p className="text-muted-foreground text-xs">{query.error.message}</p>
          <Button
            type="button"
            size="xs"
            variant="outline"
            onClick={() => {
              void query.refetch();
            }}
          >
            Try again
          </Button>
        </div>
      ) : query.data === null ? (
        <p className="text-muted-foreground mt-2 text-sm">
          This order no longer exists.
        </p>
      ) : (
        <QuickViewDetails order={query.data} />
      )}
    </div>
  );
}

/** How long a read may take before the popover stops looking like it is about to finish. */
const SLOW_AFTER_MS = 8000;

/**
 * The popover while the order has not arrived: a skeleton, for as long as that
 * is plausibly a moment.
 *
 * Two things end the skeleton without data (#517). Offline, the read never
 * starts, and a skeleton that never resolves says nothing about why — so it
 * says so, and that it will load by itself once there is a connection. And a
 * read that has simply not answered, which has no ending of its own: after a
 * while it says it is taking long and offers to ask again.
 *
 * Mounted only while pending, so each wait starts its own clock.
 */
function Pending({
  offline,
  onRetry,
}: {
  offline: boolean;
  onRetry: () => void;
}) {
  // Counts the attempts, so a retry restarts the clock.
  const [attempt, setAttempt] = useState(0);
  const [slow, setSlow] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSlow(true);
    }, SLOW_AFTER_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [attempt]);

  if (offline || slow) {
    return (
      <div className="mt-2 space-y-2">
        <p className="text-sm font-medium">
          {offline ? "You're offline" : "This is taking longer than usual"}
        </p>
        <p className="text-muted-foreground text-xs">
          {offline
            ? "Items and tracking will load by themselves once you are back online."
            : "The order has not come back yet. It may be your connection."}
        </p>
        <Button
          type="button"
          size="xs"
          variant="outline"
          className="pointer-coarse:h-9 pointer-coarse:px-3"
          onClick={() => {
            setSlow(false);
            setAttempt((count) => count + 1);
            onRetry();
          }}
        >
          Try again
        </Button>
      </div>
    );
  }

  return (
    <div className="mt-3 space-y-2" aria-hidden>
      <Skeleton className="h-4 w-4/5" />
      <Skeleton className="h-4 w-3/5" />
      <Skeleton className="h-4 w-2/3" />
    </div>
  );
}

/**
 * What is still on the order. An edit can remove a line after the sale
 * (Shopify keeps it with a current quantity of zero), so the quantity read is
 * the current one where the channel gave it — the order drawer does the same.
 */
function currentQuantity(line: OrderLineItem): number {
  const current = line.raw.currentQuantity;
  return typeof current === "number" ? current : line.quantity;
}

function QuickViewDetails({ order }: { order: OrderWithDetails }) {
  const lines = order.lineItems
    .map((line) => ({ line, quantity: currentQuantity(line) }))
    .filter((entry) => entry.quantity > 0);

  return (
    <div className="mt-2 space-y-3">
      <section>
        <h3 className="text-muted-foreground text-xs font-medium">Items</h3>
        {lines.length > 0 ? (
          <ul className="divide-y">
            {lines.map(({ line, quantity }) => (
              <li
                key={line.id}
                className="flex items-start justify-between gap-3 py-1.5"
              >
                <div className="min-w-0">
                  {/* The title already carries the variant — the API
                      writes "Product — Variant" onto it. */}
                  <p className="text-sm leading-snug wrap-anywhere">
                    {line.title}
                  </p>
                  {/* `wrap-anywhere`, here and below: a SKU or a tracking
                      number is one unbroken word, and without a place to
                      break it ran past the popover's edge and was clipped. */}
                  {line.sku ? (
                    <p className="text-muted-foreground text-xs wrap-anywhere">
                      SKU {line.sku}
                    </p>
                  ) : null}
                </div>
                <span className="text-muted-foreground shrink-0 text-sm tabular-nums">
                  × {quantity}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-muted-foreground py-1.5 text-sm">No items.</p>
        )}
      </section>

      <section>
        <h3 className="text-muted-foreground text-xs font-medium">Shipments</h3>
        {order.shipments.length > 0 ? (
          <ul className="divide-y">
            {order.shipments.map((shipment) => (
              <ShipmentItem key={shipment.id} shipment={shipment} />
            ))}
          </ul>
        ) : (
          <p className="text-muted-foreground py-1.5 text-sm">
            Not shipped yet.
          </p>
        )}
      </section>
    </div>
  );
}

function ShipmentItem({ shipment }: { shipment: ShipmentWithScans }) {
  // `null` for anything that is not http or https: the address comes from the
  // channel as written, and is shown as text rather than followed.
  const href = safeWebHref(shipment.trackingUrl);
  const cancelled = shipment.status === "cancelled";
  // The newest scan: scans arrive oldest first.
  const lastScan = shipment.scans[shipment.scans.length - 1];
  const lastWords = lastScan ? scanWords(lastScan) : null;
  const lastWhere = [lastScan?.location]
    .map((part) => part?.trim() ?? "")
    .filter((part) => part !== "");

  return (
    <li className="py-1.5">
      <p className="flex items-center gap-1.5 text-sm">
        <Truck
          className="text-muted-foreground size-3.5 shrink-0"
          aria-hidden
        />
        <span className="min-w-0 wrap-anywhere">
          {shipment.courier ?? "Courier not set"}
        </span>
        {cancelled ? (
          <span className="text-muted-foreground text-xs">Cancelled</span>
        ) : null}
      </p>
      <p className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 pl-5 text-xs">
        {shipment.trackingNumber ? (
          <span className="min-w-0 tabular-nums wrap-anywhere">
            {shipment.trackingNumber}
          </span>
        ) : (
          <span className="text-muted-foreground">No tracking number</span>
        )}
        {href ? (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Track shipment${shipment.trackingNumber ? ` ${shipment.trackingNumber}` : ""} (opens in a new tab)`}
            // Padded and pulled back by the same amount: a finger-sized target
            // on a phone without making the row taller.
            className="text-primary-strong -my-1.5 inline-flex items-center gap-1 py-1.5 underline-offset-2 hover:underline"
          >
            Track
            <ExternalLink className="size-3" aria-hidden />
          </a>
        ) : shipment.trackingNumber ? (
          <span className="text-muted-foreground">No tracking link</span>
        ) : null}
      </p>
      {/* Tracking (#527): where it is, whether it needs a look, and the last
          thing the courier said. Nothing for a parcel with no number, or none
          checked yet — the quick view is not where to explain why. */}
      {shipment.stage ? (
        <div className="mt-1 space-y-0.5 pl-5">
          <p className="flex flex-wrap items-center gap-1">
            <StageBadge stage={shipment.stage} />
            <HealthLabel health={shipment.health} />
          </p>
          {lastScan && lastWords ? (
            <p className="text-muted-foreground text-xs wrap-anywhere">
              Last scan: {lastWords.title}
              {lastWhere.length > 0 ? `, ${lastWhere.join(", ")}` : ""}
              {" · "}
              <ExactTime value={lastScan.happenedAt} />
            </p>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}
